import { useState, useEffect } from 'react';

const WMO_DESCRIPTIONS = {
  0: 'Clear Sky', 1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Icy Fog', 51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
  61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
  71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow',
  77: 'Snow Grains', 80: 'Light Showers', 81: 'Showers', 82: 'Heavy Showers',
  85: 'Snow Showers', 86: 'Heavy Snow Showers',
  95: 'Thunderstorm', 96: 'Thunderstorm w/ Hail', 99: 'Thunderstorm w/ Heavy Hail',
};

const WMO_ICONS = {
  0: '☀️', 1: '🌤', 2: '⛅️', 3: '☁️',
  45: '🌫', 48: '🌫',
  51: '🌦', 53: '🌦', 55: '🌧',
  61: '🌦', 63: '🌧', 65: '🌧',
  71: '🌨', 73: '❄️', 75: '❄️',
  77: '🌨', 80: '🌦', 81: '🌧', 82: '⛈',
  85: '🌨', 86: '❄️',
  95: '⛈', 96: '⛈', 99: '⛈',
};

// Fixed coordinates for each project — no geocoding needed
const PROJECT_WEATHER_COORDS = {
  1: { lat: 35.9245, lng: -86.9028, name: 'Franklin, TN' },
  2: { lat: 35.6151, lng: -86.9978, name: 'Columbia, TN' },
  3: { lat: 35.8168, lng: -86.1372, name: 'Bradyville, TN' },
};

// Fallback: look up coords from address string
const KNOWN_COORDS = {
  'Franklin': { lat: 35.9245, lng: -86.9028, name: 'Franklin, TN' },
  'Columbia': { lat: 35.6151, lng: -86.9978, name: 'Columbia, TN' },
  'Bradyville': { lat: 35.8168, lng: -86.1372, name: 'Bradyville, TN' },
};

export function useFullWeather(address, projectId) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      setLoading(true);

      // Try projectId lookup first (most reliable)
      let coords = null;
      if (projectId) {
        coords = PROJECT_WEATHER_COORDS[parseInt(projectId)];
      }

      // Fall back to address matching
      if (!coords && address) {
        for (const [key, c] of Object.entries(KNOWN_COORDS)) {
          if (address.includes(key)) { coords = c; break; }
        }
      }

      if (!coords) { setLoading(false); return; }

      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?` +
          `latitude=${coords.lat}&longitude=${coords.lng}` +
          `&current=temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m,wind_direction_10m,weather_code,is_day` +
          `&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,wind_speed_10m_max,sunrise,sunset` +
          `&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FChicago&forecast_days=7`
        );
        const data = await res.json();
        const c = data.current;
        const d = data.daily;

        if (!c) { setLoading(false); return; }

        const forecast = (d?.time || []).map((date, i) => ({
          date,
          dayName: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' }),
          fullDate: new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
          code: d.weather_code[i],
          icon: WMO_ICONS[d.weather_code[i]] || '🌤',
          description: WMO_DESCRIPTIONS[d.weather_code[i]] || 'Weather',
          hi: Math.round(d.temperature_2m_max[i]),
          lo: Math.round(d.temperature_2m_min[i]),
          rain: d.precipitation_probability_max[i],
          wind: Math.round(d.wind_speed_10m_max[i]),
          sunrise: d.sunrise[i]?.split('T')[1]?.slice(0,5),
          sunset: d.sunset[i]?.split('T')[1]?.slice(0,5),
        }));

        setWeather({
          current: {
            temp: Math.round(c.temperature_2m),
            feelsLike: Math.round(c.apparent_temperature),
            humidity: c.relative_humidity_2m,
            wind: Math.round(c.wind_speed_10m),
            windDir: c.wind_direction_10m,
            code: c.weather_code,
            icon: WMO_ICONS[c.weather_code] || '🌤',
            description: WMO_DESCRIPTIONS[c.weather_code] || 'Weather',
            isDay: c.is_day,
          },
          forecast,
          location: coords.name,
          lat: coords.lat,
          lng: coords.lng,
          nwsUrl: `https://forecast.weather.gov/MapClick.php?lat=${coords.lat}&lon=${coords.lng}`,
        });
      } catch(e) {
        console.error('useFullWeather error:', e);
      }
      setLoading(false);
    };

    fetchWeather();
  }, [projectId]); // re-fetch if projectId changes

  return { weather, loading };
}

export function windDirection(deg) {
  const dirs = ['N','NE','E','SE','S','SW','W','NW'];
  return dirs[Math.round(deg / 45) % 8];
}
